"use client"

import { useState, useEffect, useCallback } from "react"
import { supabase } from "../supabaseClient"
import { useAuth } from "../context/AuthContext"
import "../styles/GestaoUsuariosPage.css"

const PAPEIS_INSTITUICAO = [
  { value: "diretor_geral", label: "Diretor Geral" },
  { value: "diretor_administrativo", label: "Diretor Administrativo" },
  { value: "diretor_pedagogico", label: "Diretor Pedagogico" },
  { value: "secretaria", label: "Secretaria" },
  { value: "secretaria_2", label: "Secretaria 2" },
]

export default function GestaoUsuariosPage() {
  const { isAdmin, userProfile, instituicaoId } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [instituicoes, setInstituicoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editando, setEditando] = useState(null)
  const [erro, setErro] = useState("")
  const [sucesso, setSucesso] = useState("")
  const [tipoUsuario, setTipoUsuario] = useState("instituicao")
  
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    senha: "",
    papel: "secretaria",
    instituicao_id: "",
  })

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [usuarioToDelete, setUsuarioToDelete] = useState(null)

  const carregarDados = useCallback(async () => {
    setLoading(true)
    setErro("")
    
    console.log("[v0] Carregando dados...")

    try {
      // Carregar instituicoes primeiro
      let instQuery = supabase.from("instituicoes").select("id, nome, sigla").order("nome")
      if (!isAdmin && instituicaoId) {
        instQuery = instQuery.eq("id", instituicaoId)
      }
      const { data: instData, error: instError } = await instQuery

      console.log("[v0] Instituicoes:", { instData, instError })

      if (instError) {
        console.error("[v0] Erro ao carregar instituicoes:", instError)
      } else {
        setInstituicoes(instData || [])
      }

      // Carregar usuarios
      let query = supabase
        .from("usuarios")
        .select("*")
        .order("nome")

      if (!isAdmin && instituicaoId) {
        query = query.eq("instituicao_id", instituicaoId)
      }

      const { data: usersData, error: usersError } = await query

      console.log("[v0] Usuarios:", { usersData, usersError })

      if (usersError) {
        throw usersError
      }

      // Mapear nomes das instituicoes
      const instMap = {}
      ;(instData || []).forEach(inst => {
        instMap[inst.id] = inst.nome
      })
      
      const usuariosComInst = (usersData || []).map(user => ({
        ...user,
        instituicao_nome: user.instituicao_id ? instMap[user.instituicao_id] || "-" : "-"
      }))
      
      setUsuarios(usuariosComInst)
    } catch (error) {
      console.error("[v0] Erro ao carregar dados:", error)
      setErro("Erro ao carregar dados: " + error.message)
    } finally {
      setLoading(false)
    }
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    carregarDados()
  }, [carregarDados])

  const limparMensagens = () => {
    setErro("")
    setSucesso("")
  }

  const verificarLimiteUsuarios = async (instId) => {
    if (!instId) return { permitido: true }

    const { count } = await supabase
      .from("usuarios")
      .select("*", { count: "exact", head: true })
      .eq("instituicao_id", instId)

    const { data: instData } = await supabase
      .from("instituicoes")
      .select("max_usuarios, nome")
      .eq("id", instId)
      .single()

    const limite = instData?.max_usuarios || 5
    const atual = count || 0

    if (atual >= limite) {
      return {
        permitido: false,
        mensagem: `A instituicao "${instData?.nome}" atingiu o limite de ${limite} usuarios.`
      }
    }

    return { permitido: true }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    limparMensagens()

    if (!formData.nome.trim()) {
      setErro("O nome e obrigatorio.")
      return
    }

    if (!formData.email.trim()) {
      setErro("O email e obrigatorio.")
      return
    }

    if (!editando && !formData.senha.trim()) {
      setErro("A senha e obrigatoria para novos usuarios.")
      return
    }

    if (!editando && formData.senha.length < 6) {
      setErro("A senha deve ter pelo menos 6 caracteres.")
      return
    }

    const isUsuarioAdmin = tipoUsuario === "admin" || formData.papel === "admin"
    
    if (isUsuarioAdmin && !isAdmin) {
      setErro("Apenas administradores podem criar/editar usuarios com papel 'admin'.")
      return
    }

    if (!isUsuarioAdmin) {
      // Somente diretores (geral ou pedagogico) ou admin podem cadastrar usuarios da instituicao
      if (!isAdmin && !["diretor_geral", "diretor_pedagogico"].includes(userProfile?.papel)) {
        setErro("Apenas Diretores (Geral ou Pedagógico) podem cadastrar usuarios da instituicao.")
        return
      }
      const instId = isAdmin ? formData.instituicao_id : instituicaoId
      
      if (!instId) {
        setErro("Selecione uma instituicao para o usuario.")
        return
      }

      if (!editando) {
        const { permitido, mensagem } = await verificarLimiteUsuarios(instId)
        if (!permitido) {
          setErro(mensagem)
          return
        }
      }
    }

    console.log("[v0] Salvando usuario:", { tipoUsuario, formData, editando: !!editando })

    try {
      // Verificar se email ja existe
      if (!editando) {
        const { data: existingUser } = await supabase
          .from("usuarios")
          .select("id")
          .eq("email", formData.email.trim().toLowerCase())
          .maybeSingle()

        if (existingUser) {
          setErro("Ja existe um usuario com este email.")
          return
        }
      }

      const dadosUsuario = {
        nome: formData.nome.trim(),
        email: formData.email.trim().toLowerCase(),
        papel: isUsuarioAdmin ? "admin" : formData.papel,
        instituicao_id: isUsuarioAdmin ? null : (isAdmin ? formData.instituicao_id : instituicaoId),
        ativo: true,
      }

      if (editando) {
        const updateData = { ...dadosUsuario }
        if (formData.senha.trim()) {
          updateData.senha = formData.senha.trim()
        }

        console.log("[v0] Atualizando usuario ID:", editando.id)

        const { data, error } = await supabase
          .from("usuarios")
          .update(updateData)
          .eq("id", editando.id)
          .select()

        console.log("[v0] Resultado update:", { data, error })

        if (error) throw error
        
        if (!data || data.length === 0) {
          throw new Error("Nenhum registro foi atualizado. Verifique as permissoes do banco de dados.")
        }
        
        setSucesso("Usuario atualizado com sucesso!")
      } else {
        console.log("[v0] Inserindo novo usuario:", dadosUsuario)

        const { data, error } = await supabase
          .from("usuarios")
          .insert([{
            ...dadosUsuario,
            senha: formData.senha.trim(),
          }])
          .select()

        console.log("[v0] Resultado insert:", { data, error })

        if (error) throw error
        
        if (!data || data.length === 0) {
          throw new Error("Nenhum registro foi inserido. Verifique as permissoes do banco de dados (RLS).")
        }
        
        setSucesso("Usuario cadastrado com sucesso!")
      }

      setShowForm(false)
      setEditando(null)
      setFormData({ nome: "", email: "", senha: "", papel: "secretaria", instituicao_id: "" })
      setTipoUsuario("instituicao")
      await carregarDados()
    } catch (error) {
      console.error("[v0] Erro ao salvar usuario:", error)
      setErro("Erro ao salvar usuario: " + error.message)
    }
  }

  const handleEdit = (usuario) => {
    limparMensagens()
    setEditando(usuario)
    setTipoUsuario(usuario.papel === "admin" ? "admin" : "instituicao")
    setFormData({
      nome: usuario.nome || "",
      email: usuario.email || "",
      senha: "",
      papel: usuario.papel || "secretaria",
      instituicao_id: usuario.instituicao_id || "",
    })
    setShowForm(true)
  }

  const confirmDelete = (usuario) => {
    limparMensagens()
    if (usuario.id === userProfile?.id) {
      setErro("Voce nao pode excluir seu proprio usuario.")
      return
    }
    setUsuarioToDelete(usuario)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!usuarioToDelete) return

    try {
      const { error } = await supabase
        .from("usuarios")
        .delete()
        .eq("id", usuarioToDelete.id)

      if (error) throw error

      setSucesso("Usuario excluido com sucesso!")
      setShowDeleteModal(false)
      setUsuarioToDelete(null)
      await carregarDados()
    } catch (error) {
      console.error("[v0] Erro ao excluir usuario:", error)
      setErro("Erro ao excluir usuario: " + error.message)
    }
  }

  const handleToggleAtivo = async (usuario) => {
    if (usuario.id === userProfile?.id) {
      setErro("Voce nao pode desativar seu proprio usuario.")
      return
    }

    limparMensagens()
    
    try {
      const { data, error } = await supabase
        .from("usuarios")
        .update({ ativo: !usuario.ativo })
        .eq("id", usuario.id)
        .select()

      if (error) throw error
      
      if (!data || data.length === 0) {
        throw new Error("Nenhum registro foi atualizado.")
      }

      setSucesso(`Usuario ${!usuario.ativo ? "ativado" : "desativado"} com sucesso!`)
      await carregarDados()
    } catch (error) {
      console.error("[v0] Erro ao atualizar status:", error)
      setErro("Erro ao atualizar status: " + error.message)
    }
  }

  const handleNovoUsuario = () => {
    limparMensagens()
    setEditando(null)
    setTipoUsuario("instituicao")
    setFormData({ nome: "", email: "", senha: "", papel: "secretaria", instituicao_id: "" })
    setShowForm(true)
  }

  const getPapelLabel = (papel) => {
    const papelEncontrado = PAPEIS_INSTITUICAO.find(p => p.value === papel)
    if (papelEncontrado) return papelEncontrado.label
    if (papel === "admin") return "Administrador"
    return papel
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Gestao de Usuarios</h1>
        <button className="btn-primary" onClick={handleNovoUsuario}>
          + Novo Usuario
        </button>
      </div>

      {erro && <div className="alert alert-error">{erro}</div>}
      {sucesso && <div className="alert alert-success">{sucesso}</div>}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editando ? "Editar Usuario" : "Novo Usuario"}</h2>
            
            {!editando && (
              <div className="form-group tipo-usuario">
                <label>Tipo de Usuario:</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="tipoUsuario"
                      value="instituicao"
                      checked={tipoUsuario === "instituicao"}
                      onChange={() => {
                        setTipoUsuario("instituicao")
                        setFormData({ ...formData, papel: "secretaria" })
                      }}
                    />
                    Usuario de Instituicao
                  </label>
                      {isAdmin && (
                        <label>
                          <input
                            type="radio"
                            name="tipoUsuario"
                            value="admin"
                            checked={tipoUsuario === "admin"}
                            onChange={() => {
                              setTipoUsuario("admin")
                              setFormData({ ...formData, papel: "admin", instituicao_id: "" })
                            }}
                          />
                          Administrador do Sistema
                        </label>
                      )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome Completo *</label>
                <input
                  type="text"
                  placeholder="Nome do usuario"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editando}
                />
                {editando && <small>O email nao pode ser alterado.</small>}
              </div>

              <div className="form-group">
                <label>{editando ? "Nova Senha (deixe vazio para manter)" : "Senha *"}</label>
                <input
                  type="password"
                  placeholder={editando ? "Nova senha (opcional)" : "Minimo 6 caracteres"}
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  required={!editando}
                  minLength={editando ? 0 : 6}
                />
              </div>

              {tipoUsuario === "instituicao" && (
                <>
                  <div className="form-group">
                    <label>Cargo/Funcao *</label>
                    <select
                      value={formData.papel}
                      onChange={(e) => setFormData({ ...formData, papel: e.target.value })}
                      required
                    >
                      {PAPEIS_INSTITUICAO.map((papel) => (
                        <option key={papel.value} value={papel.value}>
                          {papel.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Instituicao *</label>
                    <select
                      value={formData.instituicao_id}
                      onChange={(e) => setFormData({ ...formData, instituicao_id: e.target.value })}
                      required
                    >
                      <option value="">Selecione a Instituicao</option>
                      {instituicoes.map((inst) => (
                        <option key={inst.id} value={inst.id}>
                          {inst.nome} {inst.sigla ? `(${inst.sigla})` : ""}
                        </option>
                      ))}
                    </select>
                    {instituicoes.length === 0 && (
                      <small className="warning-text">Nenhuma instituicao cadastrada. Cadastre uma instituicao primeiro.</small>
                    )}
                  </div>
                </>
              )}

              {tipoUsuario === "admin" && (
                <div className="admin-info">
                  <p><strong>Administrador do Sistema</strong></p>
                  <p>Este usuario tera acesso total ao sistema e nao estara vinculado a nenhuma instituicao.</p>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editando ? "Atualizar" : "Cadastrar"}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false)
                    setEditando(null)
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Confirmar Exclusao</h2>
            <p>Tem certeza que deseja excluir o usuario <strong>{usuarioToDelete?.nome}</strong>?</p>
            <p className="warning-text">Esta acao nao pode ser desfeita!</p>
            <div className="modal-actions">
              <button className="btn-delete" onClick={handleDelete}>Confirmar Exclusao</button>
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Carregando usuarios...</div>
      ) : usuarios.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum usuario cadastrado.</p>
          <button className="btn-primary" onClick={handleNovoUsuario}>Cadastrar primeiro usuario</button>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Cargo</th>
                <th>Instituicao</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((user) => (
                <tr key={user.id} className={!user.ativo ? "row-inactive" : ""}>
                  <td>{user.nome}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge badge-${user.papel}`}>
                      {getPapelLabel(user.papel)}
                    </span>
                  </td>
                  <td>{user.instituicao_nome || (user.papel === "admin" ? "Sistema" : "-")}</td>
                  <td>
                    <span className={`status ${user.ativo ? "ativo" : "inativo"}`}>
                      {user.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button className="btn-edit" onClick={() => handleEdit(user)}>Editar</button>
                    <button
                      className={`btn-toggle ${user.ativo ? "btn-disable" : "btn-enable"}`}
                      onClick={() => handleToggleAtivo(user)}
                      disabled={user.id === userProfile?.id}
                    >
                      {user.ativo ? "Desativar" : "Ativar"}
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => confirmDelete(user)}
                      disabled={user.id === userProfile?.id}
                    >
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
